import { useCallback, useState } from "react";
import {
  type CreateVaultDocumentMutation,
  type FinalizeVaultDocumentMutation,
  type VaultDocumentAttributeValueInput,
  type VaultPartETagInput,
} from "#graphql/supergraphTypes";
import { putBytesToTarget, runWithConcurrency } from "./putBytesToTarget";
import { useCreateVaultDocument } from "./useCreateVaultDocument";
import { useFinalizeVaultDocument } from "./useFinalizeVaultDocument";
import { useSignVaultDocumentParts, type VaultSignedPart } from "./useSignVaultDocumentParts";

type MultipartPlan = NonNullable<
  NonNullable<CreateVaultDocumentMutation["createVaultDocument"]>["multipartPlan"]
>;

type FinalizedDocument = FinalizeVaultDocumentMutation["finalizeVaultDocument"];

/**
 * How many part PUTs run at once. Multipart only kicks in at 100 MB, so parts
 * are large; a handful in flight saturates typical uplink without holding the
 * whole file in memory or stalling the tab.
 */
export interface UploadVaultDocumentArgs {
  spaceId: string;
  file: File;
  /** Typed attribute values (see `buildAttributeValueInputs`). */
  attributeValues?: VaultDocumentAttributeValueInput[];
  /**
   * Fraction of the bytes uploaded so far (0–1). Only meaningful on the
   * multipart path, where it fires as each part completes; the single-part path
   * reports 1 once its single PUT lands.
   */
  onProgress?: (fraction: number) => void;
}

export interface UseUploadVaultDocumentReturn {
  /**
   * Full 3-step upload: `createVaultDocument` -> PUT the bytes to the presigned
   * target -> `finalizeVaultDocument`. Resolves to the finalized document (its
   * `status` reflects the guaranteed-attribute validation: validated or
   * quarantined). Rejects (and sets `error`) if any step fails.
   */
  uploadDocument: (args: UploadVaultDocumentArgs) => Promise<FinalizedDocument | null>;
  uploading: boolean;
  error: Error | null;
}

/**
 * Byte range for one part. Parts are 1-indexed and every part is exactly
 * `partSizeBytes` except the last, which is whatever remains. Exported so the
 * boundary arithmetic is unit-testable — jsdom can't read a Blob request body,
 * so an integration test can't verify the slices it sent.
 */
export const partByteRange = (
  partNumber: number,
  partSizeBytes: number,
  fileSize: number,
): { start: number; end: number } => {
  const start = (partNumber - 1) * partSizeBytes;
  return { start, end: Math.min(start + partSizeBytes, fileSize) };
};

/**
 * PUT each slice of the file to its presigned part URL.
 *
 * Deliberately sends NO headers: the part URLs are signed without a
 * Content-Type, and `fetch` only adds one when the body Blob has a non-empty
 * `type` — which `File.slice()` leaves blank. Adding a Content-Type the
 * signature doesn't cover makes S3 reject the PUT with 403.
 *
 * Parts run `PUT_CONCURRENCY` at a time via the shared queue, so only a few
 * slices are ever resident in memory regardless of how large the file is.
 */
const putPartsToTargets = async (
  plan: MultipartPlan,
  file: File,
  signedParts: VaultSignedPart[],
  onProgress?: (fraction: number) => void,
): Promise<VaultPartETagInput[]> => {
  const urlByPart = new Map(signedParts.map((p) => [p.partNumber, p.url]));
  const partNumbers = Array.from({ length: plan.partCount }, (_, i) => i + 1);
  // Filled positionally: parts settle out of order, but finalize needs 1..N.
  const parts = new Array<VaultPartETagInput | null>(plan.partCount).fill(null);

  const outcomes = await runWithConcurrency(
    partNumbers,
    async (partNumber, index) => {
      const url = urlByPart.get(partNumber);
      if (!url) throw new Error(`No signed URL returned for part ${partNumber}`);
      const { start, end } = partByteRange(partNumber, plan.partSizeBytes, file.size);
      // Deliberately NO headers (see above): the part URLs are signed without a
      // Content-Type, and `File.slice()` leaves the Blob's type blank.
      const response = await fetch(url, { method: "PUT", body: file.slice(start, end) });
      if (!response.ok) {
        throw new Error(
          `Vault upload failed on part ${partNumber} of ${plan.partCount} ` +
            `(${response.status} ${response.statusText})`,
        );
      }
      const etag = response.headers.get("ETag");
      if (!etag) {
        throw new Error(
          `No ETag on part ${partNumber} of ${plan.partCount} — the bucket CORS ` +
            `policy must expose the ETag response header`,
        );
      }
      parts[index] = { partNumber, etag };
    },
    (completed, total) => onProgress?.(completed / total),
  );

  // Multipart IS all-or-nothing — a missing part means the object can't be
  // assembled — so the first failure is rethrown once every worker has settled.
  const failure = outcomes.find((o): o is Error => o !== null);
  if (failure) throw failure;

  return parts.filter((part): part is VaultPartETagInput => part !== null);
};

/**
 * DV-245 / DV-250 — orchestrates the presentational upload flow end to end. The
 * middle step is real network I/O (a PUT of the file bytes), so this hook owns
 * the whole sequence rather than leaving it split across the caller.
 *
 * @example
 * const { uploadDocument, uploading } = useUploadVaultDocument();
 * await uploadDocument({ spaceId, file, attributeValues });
 */
export const useUploadVaultDocument = (): UseUploadVaultDocumentReturn => {
  const { createVaultDocument } = useCreateVaultDocument();
  const { finalizeVaultDocument } = useFinalizeVaultDocument();
  const { signVaultDocumentParts } = useSignVaultDocumentParts();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const uploadDocument = useCallback(
    async ({ spaceId, file, attributeValues = [], onProgress }: UploadVaultDocumentArgs) => {
      setUploading(true);
      setError(null);
      try {
        const created = await createVaultDocument({
          spaceId,
          filename: file.name,
          contentType: file.type || "application/octet-stream",
          sizeBytes: file.size,
          attributeValues,
        });

        // DSS picks the path from the size (multipart from 100 MB up) and returns
        // exactly one of the two. Storage rejects `parts` on a single-part version
        // just as it rejects their absence on a multipart one.
        let publicRefId: string;
        let ssbVersionNumber: number;
        let parts: VaultPartETagInput[] | undefined;

        if (created?.uploadTarget) {
          await putBytesToTarget(created.uploadTarget, file);
          onProgress?.(1);
          publicRefId = created.uploadTarget.publicRefId;
          ssbVersionNumber = created.uploadTarget.ssbVersionNumber;
        } else if (created?.multipartPlan) {
          const plan = created.multipartPlan;
          const signedParts = await signVaultDocumentParts({
            publicRefId: plan.publicRefId,
            ssbVersionNumber: plan.ssbVersionNumber,
            partNumbers: Array.from({ length: plan.partCount }, (_, i) => i + 1),
          });
          parts = await putPartsToTargets(plan, file, signedParts, onProgress);
          publicRefId = plan.publicRefId;
          ssbVersionNumber = plan.ssbVersionNumber;
        } else {
          throw new Error(
            "createVaultDocument returned neither an upload target nor a multipart plan",
          );
        }

        return await finalizeVaultDocument({ publicRefId, ssbVersionNumber, parts });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [createVaultDocument, signVaultDocumentParts, finalizeVaultDocument],
  );

  return { uploadDocument, uploading, error };
};
